import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Plus, Tag, Gift, X, Pencil, History, Send, Ban } from "lucide-react";
// Relative: pure modules and presentational components, resolvable under Jest.
import useAdminLoad from "../hooks/useAdminLoad";
import LoadFailure from "../components/ui/LoadFailure";
import Pager from "../components/ui/Pager";
import ExpandableText from "../components/ui/ExpandableText";
import { extractApiError } from "../lib/apiErrors";
import { formatMoneyIntl } from "../lib/formatMoney";
import { OPERATING_CURRENCIES as CURRENCIES, currencyLabel } from "../lib/currencies";
import { formatDate } from "../lib/dateTime";
import { PLATFORM_OFFERS as IDS } from "../constants/testIds/platformOffers";
import {
  listPromos, createPromo, updatePromo, promoRedemptions,
  listGiftCards, createGiftCard, resendGiftCard, voidGiftCard, giftCardRedemptions,
} from "../platformOffers/api";
import { platformOfferErrorMessage } from "../platformOffers/errors";
import { useScreenWrites } from "./screenWrites";
import { usePlanNames } from "./hq/usePlanNames";
import { majorToCents, percentToBp, bpToPercent } from "../platformOffers/money";
import {
  discountText, durationText,
  REDEMPTION_STATUS_KEYS, REDEMPTION_STATUS_TONES, RELEASE_REASON_KEYS, SURFACE_KEYS,
} from "../platformOffers/describe";

/**
 * Super admin → Gift & Promo: the platform's own promo codes and gift cards, spent against
 * a practice's Solunary subscription (docs/payment-module/platform-offers-api-contract.md §4).
 *
 * A promo's TERMS (type, amount or percent, currency, duration) are fixed at creation —
 * they are mirrored into a Stripe coupon, which cannot be re-priced — so the edit form
 * shows them read-only and PATCHes only what may change. Nothing is deleted: a promo is
 * archived with its Active switch, a gift card is voided with a reason.
 */

const PAGE_SIZE = 50;
const LOCALES = ["fr", "en", "es"];
const DURATIONS = ["once", "repeating", "forever"];
// Server bounds (§3.1, §4.2), mirrored so the form refuses first; the server still decides.
const CODE_PATTERN = /^[A-Za-z0-9_-]{2,64}$/;
const GIFT_MIN_CENTS = 100;
const GIFT_MAX_CENTS = 100000;
const MONTHS_MAX = 36;

const GIFT_STATUS_TONES = {
  active: "bg-emerald-100 text-emerald-600",
  held: "bg-amber-100 text-amber-700",
  redeemed: "bg-sky-100 text-sky-700",
  voided: "bg-slate-100 text-slate-500",
};

const FIELD = "bg-white border border-slate-200 rounded-md px-3 py-2 text-sm w-full disabled:bg-slate-50 disabled:text-slate-400";

/** ISO instant -> the "YYYY-MM-DDTHH:mm" a datetime-local input shows, in local time. */
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** A datetime-local value (local time) -> ISO-8601 UTC, or null when blank. */
const fromLocalInput = (value) => (value ? new Date(value).toISOString() : null);

/** A positive integer typed into a field, or null for blank; NaN when it is not one. */
const optionalPositiveInt = (value) => {
  const s = String(value ?? "").trim();
  if (!s) return null;
  return /^\d+$/.test(s) && Number(s) >= 1 ? Number(s) : NaN;
};

function useDateText() {
  const { lang } = useLanguage();
  return (iso) => formatDate(iso, lang);
}

/** The "Created" cell of both lists: the date, then the operator who created the row. */
function CreatedCell({ createdAt, createdBy, dateText, t, testId }) {
  return (
    <td className="px-4 py-3 text-slate-500 whitespace-nowrap" data-testid={testId}>
      {dateText(createdAt)}
      <div className="text-xs text-slate-400 break-all whitespace-normal">{createdBy || t("gp.createdByUnknown")}</div>
    </td>
  );
}

export default function HqGiftPromo() {
  const t = useT("superadmin");
  const [tab, setTab] = useState("promos");
  const [drawer, setDrawer] = useState(null); // {title, load}
  return (
    <div data-testid={IDS.page}>
      <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("gp.title")}</h1>
      <p className="text-slate-500 mb-6">{t("gp.subtitle")}</p>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("promos")} data-testid={IDS.tabPromos}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm ${tab === "promos" ? "bg-sol-gold/15 text-amber-600 border border-sol-gold/30" : "bg-white text-slate-500 border border-slate-200"}`}>
          <Tag className="w-4 h-4" /> {t("gp.tabPromos")}
        </button>
        <button onClick={() => setTab("gifts")} data-testid={IDS.tabGifts}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm ${tab === "gifts" ? "bg-sol-gold/15 text-amber-600 border border-sol-gold/30" : "bg-white text-slate-500 border border-slate-200"}`}>
          <Gift className="w-4 h-4" /> {t("gp.tabGifts")}
        </button>
      </div>
      {tab === "promos" ? <PromosTab t={t} onRedemptions={setDrawer} /> : <GiftsTab t={t} onRedemptions={setDrawer} />}
      {drawer && <RedemptionsDrawer {...drawer} onClose={() => setDrawer(null)} t={t} />}
    </div>
  );
}

// ── promos ───────────────────────────────────────────────────────────────────

/** One page of a §4 list, held with its read state. */
function usePagedList(fetchPage) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, items: [] });
  const load = useCallback(async () => {
    setData(await fetchPage({ limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE }));
  }, [fetchPage, page]);
  const { loading, loadError, reload } = useAdminLoad(load);
  return { page, setPage, data, loading, loadError, reload };
}

function ListPager({ list, testId, t }) {
  if (list.data.total <= PAGE_SIZE) return null;
  return (
    <Pager
      page={list.page}
      pageSize={PAGE_SIZE}
      total={list.data.total}
      shown={list.data.items.length}
      onPage={list.setPage}
      infoText={(v) => t("gp.pagerInfo", v)}
      prevLabel={t("gp.pagerPrev")}
      nextLabel={t("gp.pagerNext")}
      testId={testId}
      tone="slate"
    />
  );
}

function PromosTab({ t, onRedemptions }) {
  const tc = useT("common");
  const { lang } = useLanguage();
  const dateText = useDateText();
  const list = usePagedList(listPromos);
  // Creating, editing and archiving a promo are settings.write. The form's plan picker reads
  // GET /super-admin/plans, which is super admin only: it is read only by someone who can both
  // write promos and read it, never fired to collect a 403.
  const { manage: canManage, planCatalogue } = useScreenWrites("giftPromo");
  // The table names plans from the public plan list, which every operator can read.
  const planName = usePlanNames(lang);
  const [plans, setPlans] = useState(null); // null = not loaded, [] = unavailable or none
  const [plansNote, setPlansNote] = useState(""); // why the picker has no plans: a gp.* key, or ""
  const [form, setForm] = useState(null); // null | {mode: "create"} | {mode: "edit", promo}
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    if (!canManage) return;
    if (!planCatalogue) { setPlans([]); setPlansNote("gp.plansUnavailable"); return; }
    api.get("/super-admin/plans")
      .then(({ data }) => setPlans(Array.isArray(data) ? data : []))
      .catch(() => { setPlans([]); setPlansNote("gp.plansLoadFailed"); });
  }, [canManage, planCatalogue]);

  const toggleActive = async (promo) => {
    setToggling(promo.promo_id);
    try {
      await updatePromo(promo.promo_id, { active: !promo.active });
      toast.success(t(promo.active ? "gp.promoArchived" : "gp.promoReactivated"));
      list.reload();
    } catch (err) {
      toast.error(platformOfferErrorMessage(err, tc, t("gp.error")));
    } finally {
      setToggling(null);
    }
  };

  const saved = () => { setForm(null); list.reload(); };

  return (
    <div>
      {canManage && (
        <div className="flex justify-end mb-4">
          <Button onClick={() => setForm(form?.mode === "create" ? null : { mode: "create" })}
            className="bg-sol-gold text-black hover:bg-amber-200" data-testid={IDS.newPromo}>
            <Plus className="w-4 h-4 mr-2" /> {t("gp.newPromo")}
          </Button>
        </div>
      )}
      {canManage && form && (
        <PromoForm
          key={form.mode === "edit" ? form.promo.promo_id : "create"}
          promo={form.mode === "edit" ? form.promo : null}
          plans={plans || []}
          plansNote={plansNote}
          onSaved={saved}
          onCancel={() => setForm(null)}
          t={t}
        />
      )}
      <LoadFailure
        message={list.loadError ? extractApiError(list.loadError, tc("errors.loadFailed"), tc) : ""}
        onRetry={list.reload}
        retryLabel={tc("actions.retry")}
        testId="promo-list-load-error"
        className="mb-4"
      />
      <div className="rounded-2xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-400 tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">{t("gp.colCode")}</th>
              <th className="text-left px-4 py-3">{t("gp.colDiscount")}</th>
              <th className="text-left px-4 py-3">{t("gp.colPlans")}</th>
              <th className="text-left px-4 py-3">{t("gp.colUses")}</th>
              <th className="text-left px-4 py-3">{t("gp.colExpires")}</th>
              <th className="text-left px-4 py-3">{t("gp.colCreated")}</th>
              <th className="text-left px-4 py-3">{t("gp.colStatus")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((p) => (
              <tr key={p.promo_id} className="border-t border-slate-100 align-top" data-testid={IDS.promoRow(p.promo_id)}>
                <td className="px-4 py-3">
                  <code className="text-amber-600 font-medium">{p.code}</code>
                  <ExpandableText
                    text={p.description}
                    lines={2}
                    moreLabel={tc("actions.showMore")}
                    lessLabel={tc("actions.showLess")}
                    className="text-xs text-slate-500 mt-0.5 max-w-[20rem]"
                    testId={IDS.promoDescription(p.promo_id)}
                  />
                </td>
                <td className="px-4 py-3">
                  {discountText(p, tc, lang)}
                  <div className="text-xs text-slate-400">{durationText(p, tc)}</div>
                </td>
                <td className="px-4 py-3 text-slate-500">
                  {p.plan_ids ? p.plan_ids.map(planName).join(", ") : t("gp.allPlans")}
                </td>
                <td className="px-4 py-3 text-slate-500 tabular-nums">{p.used_count}/{p.max_uses ?? "∞"}</td>
                <td className="px-4 py-3 text-slate-500">{p.expires_at ? dateText(p.expires_at) : t("gp.never")}</td>
                <CreatedCell createdAt={p.created_at} createdBy={p.created_by} dateText={dateText} t={t} testId={IDS.promoCreated(p.promo_id)} />
                <td className="px-4 py-3">
                  {canManage ? (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={p.active}
                    onClick={() => toggleActive(p)}
                    disabled={toggling === p.promo_id}
                    data-testid={IDS.promoToggleActive(p.promo_id)}
                    className={`text-xs rounded-full px-2 py-0.5 disabled:opacity-50 ${p.active ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500"}`}
                  >
                    {p.active ? t("gp.active") : t("gp.inactive")}
                  </button>
                  ) : (
                    <span className={`text-xs rounded-full px-2 py-0.5 ${p.active ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                      {p.active ? t("gp.active") : t("gp.inactive")}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {canManage && (
                    <button type="button" onClick={() => setForm({ mode: "edit", promo: p })}
                      data-testid={IDS.promoEdit(p.promo_id)} title={t("gp.edit")} aria-label={t("gp.edit")}
                      className="text-slate-400 hover:text-slate-700 mr-3"><Pencil className="w-3.5 h-3.5" /></button>
                  )}
                  <button type="button"
                    onClick={() => onRedemptions({ title: p.code, load: () => promoRedemptions(p.promo_id) })}
                    data-testid={IDS.promoRedemptions(p.promo_id)} title={t("gp.redemptions")} aria-label={t("gp.redemptions")}
                    className="text-slate-400 hover:text-slate-700"><History className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {!list.loading && !list.loadError && list.data.items.length === 0 && (
              <tr><td colSpan="8" className="text-center px-4 py-10 text-slate-400">{t("gp.emptyPromo")}</td></tr>
            )}
          </tbody>
        </table>
        <ListPager list={list} testId="promo-pager" t={t} />
      </div>
    </div>
  );
}

/** The create form's initial draft, or the edit form's draft of an existing promo. */
const promoDraft = (promo) => (promo
  ? {
      code: promo.code,
      discount_type: promo.discount_type,
      percent: promo.percent_bp != null ? bpToPercent(promo.percent_bp) : "",
      amount: promo.amount_cents != null ? (promo.amount_cents / 100).toFixed(2) : "",
      currency: promo.currency || CURRENCIES[0],
      duration: promo.duration,
      duration_in_months: promo.duration_in_months != null ? String(promo.duration_in_months) : "",
      max_uses: promo.max_uses != null ? String(promo.max_uses) : "",
      expires_at: toLocalInput(promo.expires_at),
      plan_ids: promo.plan_ids || [],
      description: promo.description || "",
    }
  : {
      code: "", discount_type: "percent", percent: "", amount: "", currency: CURRENCIES[0],
      duration: "once", duration_in_months: "", max_uses: "", expires_at: "", plan_ids: [],
      description: "",
    });

/**
 * The draft as the wire's fields, or the i18n key of the first thing wrong with it.
 * Mirrors §3.1's PROMO_TERMS_INVALID rules; the server remains the authority.
 * `checkExpiry` is false when an edit leaves the date untouched: a promo that has
 * already expired must still accept a new description.
 */
function promoFields(d, { checkExpiry = true } = {}) {
  const isPercent = d.discount_type === "percent";
  const percentBp = isPercent ? percentToBp(d.percent) : null;
  const amountCents = isPercent ? null : majorToCents(d.amount);
  const months = d.duration === "repeating" ? optionalPositiveInt(d.duration_in_months) : null;
  const maxUses = optionalPositiveInt(d.max_uses);
  const expiresAt = fromLocalInput(d.expires_at);
  const code = d.code.trim();

  if (!CODE_PATTERN.test(code)) return { error: "gp.valid.code" };
  if (code.toUpperCase().startsWith("GC-")) return { error: "gp.valid.codeGiftPrefix" };
  if (isPercent && !(percentBp >= 1 && percentBp <= 10000)) return { error: "gp.valid.percent" };
  if (!isPercent && !(amountCents >= 1)) return { error: "gp.valid.amount" };
  if (d.duration === "repeating" && !(months >= 1 && months <= MONTHS_MAX)) return { error: "gp.valid.months" };
  if (Number.isNaN(maxUses)) return { error: "gp.valid.maxUses" };
  if (checkExpiry && expiresAt && new Date(expiresAt) <= new Date()) return { error: "gp.valid.expiresPast" };

  return {
    fields: {
      code,
      discount_type: d.discount_type,
      percent_bp: percentBp,
      amount_cents: amountCents,
      currency: isPercent ? null : d.currency,
      duration: d.duration,
      duration_in_months: months,
      plan_ids: d.plan_ids.length ? d.plan_ids : null,
      max_uses: maxUses,
      expires_at: expiresAt,
      description: d.description.trim(),
    },
  };
}

// The only fields a PATCH may carry (§4.1); `active` has its own switch in the table.
const EDITABLE = ["max_uses", "expires_at", "description", "plan_ids"];

/**
 * Has the operator changed draft field `k`? Compared at DRAFT level, against the draft
 * the promo itself produces: the wire value would differ on noise alone (a datetime
 * input drops seconds, a multi-select reorders plan ids).
 */
const draftChanged = (k, draft, original) => {
  const norm = (v) => (Array.isArray(v) ? [...v].sort().join(",") : String(v).trim());
  return norm(draft[k]) !== norm(original[k]);
};

function PromoForm({ promo, plans, plansNote, onSaved, onCancel, t }) {
  const tc = useT("common");
  const editing = Boolean(promo);
  const [d, setD] = useState(() => promoDraft(promo));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => { setD({ ...d, [k]: e.target.value }); setError(null); };
  const isPercent = d.discount_type === "percent";

  const submit = async (e) => {
    e.preventDefault();
    const original = editing ? promoDraft(promo) : null;
    const { fields, error: invalid } = promoFields(d, {
      checkExpiry: !editing || draftChanged("expires_at", d, original),
    });
    if (invalid) { setError(t(invalid)); return; }
    setSaving(true);
    try {
      if (editing) {
        const patch = {};
        for (const k of EDITABLE) if (draftChanged(k, d, original)) patch[k] = fields[k];
        if (Object.keys(patch).length) {
          await updatePromo(promo.promo_id, patch);
          toast.success(t("gp.promoUpdated"));
        }
      } else {
        await createPromo({ ...fields, active: true });
        toast.success(t("gp.promoCreated"));
      }
      onSaved();
    } catch (err) {
      setError(platformOfferErrorMessage(err, tc, t("gp.error")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid md:grid-cols-3 gap-3 mb-6 p-6 rounded-2xl bg-sol-gold/5 border border-sol-gold/20" data-testid={IDS.promoForm}>
      <div className="md:col-span-3 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800">{editing ? t("gp.editPromo", { code: promo.code }) : t("gp.newPromo")}</h2>
        <button type="button" onClick={onCancel} aria-label={t("gp.cancel")} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
      </div>
      {editing && <p className="md:col-span-3 text-xs text-slate-500">{t("gp.termsLocked")}</p>}

      <Labeled label={t("gp.fieldCode")}>
        <Input required value={d.code} onChange={set("code")} disabled={editing} maxLength={64}
          placeholder={t("gp.phCode")} className="bg-white border-slate-200 uppercase" data-testid={IDS.promoField("code")} />
      </Labeled>
      <Labeled label={t("gp.fieldType")}>
        <select value={d.discount_type} onChange={set("discount_type")} disabled={editing} className={FIELD} data-testid={IDS.promoField("discount_type")}>
          <option value="percent">{t("gp.percentOff")}</option>
          <option value="amount">{t("gp.amountOff")}</option>
        </select>
      </Labeled>
      {isPercent ? (
        <Labeled label={t("gp.fieldPercent")}>
          <Input required inputMode="decimal" value={d.percent} onChange={set("percent")} disabled={editing}
            placeholder="20" className="bg-white border-slate-200" data-testid={IDS.promoField("percent")} />
        </Labeled>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Labeled label={t("gp.fieldAmount")}>
            <Input required inputMode="decimal" value={d.amount} onChange={set("amount")} disabled={editing}
              placeholder="10.00" className="bg-white border-slate-200" data-testid={IDS.promoField("amount")} />
          </Labeled>
          <Labeled label={t("gp.fieldCurrency")}>
            <select value={d.currency} onChange={set("currency")} disabled={editing} className={FIELD} data-testid={IDS.promoField("currency")}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{currencyLabel(c)}</option>)}
            </select>
          </Labeled>
        </div>
      )}
      <Labeled label={t("gp.fieldDuration")}>
        <select value={d.duration} onChange={set("duration")} disabled={editing} className={FIELD} data-testid={IDS.promoField("duration")}>
          {DURATIONS.map((k) => <option key={k} value={k}>{t(`gp.duration.${k}`)}</option>)}
        </select>
      </Labeled>
      {d.duration === "repeating" && (
        <Labeled label={t("gp.fieldMonths")}>
          <Input required type="number" min={1} max={MONTHS_MAX} step={1} value={d.duration_in_months}
            onChange={set("duration_in_months")} disabled={editing}
            className="bg-white border-slate-200" data-testid={IDS.promoField("duration_in_months")} />
        </Labeled>
      )}
      <Labeled label={t("gp.fieldMaxUses")} hint={editing ? t("gp.maxUsesHintUsed", { used: promo.used_count }) : t("gp.maxUsesHint")}>
        <Input type="number" min={1} step={1} value={d.max_uses} onChange={set("max_uses")}
          placeholder="∞" className="bg-white border-slate-200" data-testid={IDS.promoField("max_uses")} />
      </Labeled>
      <Labeled label={t("gp.fieldExpires")} hint={t("gp.expiresHint")}>
        <Input type="datetime-local" value={d.expires_at} onChange={set("expires_at")}
          className="bg-white border-slate-200" data-testid={IDS.promoField("expires_at")} />
      </Labeled>
      <Labeled label={t("gp.fieldPlans")} hint={plansNote ? t(plansNote) : t("gp.plansHint")}>
        <select multiple value={d.plan_ids}
          onChange={(e) => { setD({ ...d, plan_ids: Array.from(e.target.selectedOptions, (o) => o.value) }); setError(null); }}
          className={`${FIELD} h-24`} data-testid={IDS.promoField("plan_ids")}>
          {plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}
        </select>
      </Labeled>
      <div className="md:col-span-3">
        <Labeled label={t("gp.fieldDescription")}>
          <textarea value={d.description} onChange={set("description")} maxLength={500} rows={2}
            placeholder={t("gp.phDescription")} className={FIELD} data-testid={IDS.promoField("description")} />
        </Labeled>
      </div>
      {error && <p role="alert" className="md:col-span-3 text-sm text-rose-600" data-testid="promo-form-error">{error}</p>}
      <Button type="submit" disabled={saving} className="bg-sol-gold text-black md:col-span-3" data-testid={IDS.promoSubmit}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? t("gp.save") : t("gp.create")}
      </Button>
    </form>
  );
}

function Labeled({ label, hint, children }) {
  return (
    <label className="block text-xs text-slate-500">
      <span className="block mb-1 font-medium">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

// ── gift cards ───────────────────────────────────────────────────────────────

function GiftsTab({ t, onRedemptions }) {
  const tc = useT("common");
  // Issuing, resending and voiding a gift card are settings.write.
  const { manage: canManage } = useScreenWrites("giftPromo");
  const { lang } = useLanguage();
  const dateText = useDateText();
  const list = usePagedList(listGiftCards);
  const [creating, setCreating] = useState(false);
  const [voiding, setVoiding] = useState(null);
  const [resending, setResending] = useState(null);

  const delivered = (card) => Boolean(card.delivery?.sent_at);

  const resend = async (card) => {
    setResending(card.gift_card_id);
    try {
      const updated = await resendGiftCard(card.gift_card_id);
      if (delivered(updated) && !updated.delivery.last_error) toast.success(t("gp.giftResent"));
      else toast.warning(t("gp.giftDeliveryFailed"));
      list.reload();
    } catch (err) {
      toast.error(platformOfferErrorMessage(err, tc, t("gp.error")));
    } finally {
      setResending(null);
    }
  };

  return (
    <div>
      {canManage && (
        <div className="flex justify-end mb-4">
          <Button onClick={() => setCreating(!creating)} className="bg-sol-gold text-black hover:bg-amber-200" data-testid={IDS.newGift}>
            <Plus className="w-4 h-4 mr-2" /> {t("gp.newGift")}
          </Button>
        </div>
      )}
      {canManage && creating && (
        <GiftForm onSaved={() => { setCreating(false); list.reload(); }} onCancel={() => setCreating(false)} t={t} />
      )}
      <LoadFailure
        message={list.loadError ? extractApiError(list.loadError, tc("errors.loadFailed"), tc) : ""}
        onRetry={list.reload}
        retryLabel={tc("actions.retry")}
        testId="gift-list-load-error"
        className="mb-4"
      />
      <div className="rounded-2xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-400 tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">{t("gp.colCode")}</th>
              <th className="text-left px-4 py-3">{t("gp.colAmount")}</th>
              <th className="text-left px-4 py-3">{t("gp.colBalance")}</th>
              <th className="text-left px-4 py-3">{t("gp.colRecipient")}</th>
              <th className="text-left px-4 py-3">{t("gp.colStatus")}</th>
              <th className="text-left px-4 py-3">{t("gp.colDelivery")}</th>
              <th className="text-left px-4 py-3">{t("gp.colCreated")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((g) => (
              <tr key={g.gift_card_id} className="border-t border-slate-100 align-top" data-testid={IDS.giftRow(g.gift_card_id)}>
                <td className="px-4 py-3"><code className="text-amber-600 font-medium">{g.code}</code></td>
                <td className="px-4 py-3 tabular-nums">{formatMoneyIntl(g.amount_cents, g.currency, lang)}</td>
                <td className="px-4 py-3 text-slate-500 tabular-nums">{formatMoneyIntl(g.balance_cents, g.currency, lang)}</td>
                <td className="px-4 py-3 text-slate-500">
                  {g.recipient_email}
                  {g.recipient_name && <div className="text-xs text-slate-400">{g.recipient_name}</div>}
                  {g.message && g.message.trim() ? (
                    <>
                      <span className="sr-only">{t("gp.colMessage")}: </span>
                      <ExpandableText
                        text={g.message}
                        lines={3}
                        moreLabel={tc("actions.showMore")}
                        lessLabel={tc("actions.showLess")}
                        className="text-xs text-slate-500 italic mt-1 max-w-[20rem]"
                        testId={IDS.giftMessage(g.gift_card_id)}
                      />
                    </>
                  ) : (
                    <div className="text-xs text-slate-400 mt-1" data-empty="true" data-testid={IDS.giftMessage(g.gift_card_id)}>
                      {t("gp.noMessage")}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span data-testid={IDS.giftStatus(g.gift_card_id)} data-status={g.status}
                    className={`text-xs rounded-full px-2 py-0.5 ${GIFT_STATUS_TONES[g.status]}`}>
                    {t(`gp.giftStatus.${g.status}`)}
                  </span>
                  {g.status === "voided" && g.void_reason && (
                    <div className="text-xs text-slate-400 mt-1 max-w-[12rem]">{g.void_reason}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span data-testid={IDS.giftDelivery(g.gift_card_id)} data-delivered={String(delivered(g))}
                    className={`text-xs rounded-full px-2 py-0.5 ${delivered(g) ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
                    {delivered(g) ? t("gp.delivered") : t("gp.notDelivered")}
                  </span>
                  <div className="text-xs text-slate-400 mt-1">{t("gp.attempts", { n: g.delivery.attempts })}</div>
                </td>
                <CreatedCell createdAt={g.created_at} createdBy={g.created_by} dateText={dateText} t={t} testId={IDS.giftCreated(g.gift_card_id)} />
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {canManage && g.status !== "voided" && (
                    <button type="button" onClick={() => resend(g)} disabled={resending === g.gift_card_id}
                      data-testid={IDS.giftResend(g.gift_card_id)} title={t("gp.resend")} aria-label={t("gp.resend")}
                      className="text-slate-400 hover:text-slate-700 mr-3 disabled:opacity-40"><Send className="w-3.5 h-3.5" /></button>
                  )}
                  {canManage && g.status === "active" && (
                    <button type="button" onClick={() => setVoiding(g)}
                      data-testid={IDS.giftVoid(g.gift_card_id)} title={t("gp.void")} aria-label={t("gp.void")}
                      className="text-rose-400 hover:text-rose-600 mr-3"><Ban className="w-3.5 h-3.5" /></button>
                  )}
                  <button type="button"
                    onClick={() => onRedemptions({ title: g.code, load: () => giftCardRedemptions(g.gift_card_id) })}
                    data-testid={IDS.giftRedemptions(g.gift_card_id)} title={t("gp.redemptions")} aria-label={t("gp.redemptions")}
                    className="text-slate-400 hover:text-slate-700"><History className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {!list.loading && !list.loadError && list.data.items.length === 0 && (
              <tr><td colSpan="8" className="text-center px-4 py-10 text-slate-400">{t("gp.emptyGift")}</td></tr>
            )}
          </tbody>
        </table>
        <ListPager list={list} testId="gift-pager" t={t} />
      </div>
      {voiding && (
        <VoidDialog card={voiding} onDone={() => { setVoiding(null); list.reload(); }} onCancel={() => setVoiding(null)} t={t} />
      )}
    </div>
  );
}

function GiftForm({ onSaved, onCancel, t }) {
  const tc = useT("common");
  const { lang } = useLanguage();
  const [d, setD] = useState({
    amount: "", currency: CURRENCIES[0], recipient_email: "", recipient_name: "",
    message: "", locale: LOCALES.includes(lang) ? lang : LOCALES[0], expires_at: "",
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => { setD({ ...d, [k]: e.target.value }); setError(null); };

  const submit = async (e) => {
    e.preventDefault();
    const amountCents = majorToCents(d.amount);
    const expiresAt = fromLocalInput(d.expires_at);
    if (!(amountCents >= GIFT_MIN_CENTS && amountCents <= GIFT_MAX_CENTS)) {
      setError(t("gp.valid.giftAmount", {
        min: formatMoneyIntl(GIFT_MIN_CENTS, d.currency, lang),
        max: formatMoneyIntl(GIFT_MAX_CENTS, d.currency, lang),
      }));
      return;
    }
    if (expiresAt && new Date(expiresAt) <= new Date()) { setError(t("gp.valid.expiresPast")); return; }
    setSaving(true);
    try {
      const card = await createGiftCard({
        amount_cents: amountCents,
        currency: d.currency,
        recipient_email: d.recipient_email.trim(),
        recipient_name: d.recipient_name.trim(),
        message: d.message.trim(),
        locale: d.locale,
        expires_at: expiresAt,
      });
      // A failed delivery does not roll the card back (§4.2): it exists, and Resend is
      // how it reaches its recipient.
      if (card.delivery.sent_at) toast.success(t("gp.giftCreated"));
      else toast.warning(t("gp.giftCreatedUndelivered"));
      onSaved();
    } catch (err) {
      setError(platformOfferErrorMessage(err, tc, t("gp.error")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid md:grid-cols-3 gap-3 mb-6 p-6 rounded-2xl bg-sol-gold/5 border border-sol-gold/20" data-testid={IDS.giftForm}>
      <div className="md:col-span-3 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800">{t("gp.newGift")}</h2>
        <button type="button" onClick={onCancel} aria-label={t("gp.cancel")} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Labeled label={t("gp.fieldAmount")}>
          <Input required inputMode="decimal" value={d.amount} onChange={set("amount")} placeholder="50.00"
            className="bg-white border-slate-200" data-testid={IDS.giftField("amount")} />
        </Labeled>
        <Labeled label={t("gp.fieldCurrency")}>
          <select value={d.currency} onChange={set("currency")} className={FIELD} data-testid={IDS.giftField("currency")}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{currencyLabel(c)}</option>)}
          </select>
        </Labeled>
      </div>
      <Labeled label={t("gp.fieldRecipientEmail")}>
        <Input required type="email" value={d.recipient_email} onChange={set("recipient_email")}
          placeholder={t("gp.phRecipientEmail")} className="bg-white border-slate-200" data-testid={IDS.giftField("recipient_email")} />
      </Labeled>
      <Labeled label={t("gp.fieldRecipientName")}>
        <Input value={d.recipient_name} onChange={set("recipient_name")} placeholder={t("gp.phRecipientName")}
          className="bg-white border-slate-200" data-testid={IDS.giftField("recipient_name")} />
      </Labeled>
      <Labeled label={t("gp.fieldLocale")} hint={t("gp.localeHint")}>
        <select value={d.locale} onChange={set("locale")} className={FIELD} data-testid={IDS.giftField("locale")}>
          {LOCALES.map((l) => <option key={l} value={l}>{t(`gp.locale.${l}`)}</option>)}
        </select>
      </Labeled>
      <Labeled label={t("gp.fieldExpires")} hint={t("gp.expiresHint")}>
        <Input type="datetime-local" value={d.expires_at} onChange={set("expires_at")}
          className="bg-white border-slate-200" data-testid={IDS.giftField("expires_at")} />
      </Labeled>
      <div className="md:col-span-3">
        <Labeled label={t("gp.fieldMessage")}>
          <textarea value={d.message} onChange={set("message")} maxLength={1000} rows={2}
            placeholder={t("gp.phMessage")} className={FIELD} data-testid={IDS.giftField("message")} />
        </Labeled>
      </div>
      {error && <p role="alert" className="md:col-span-3 text-sm text-rose-600" data-testid="gift-form-error">{error}</p>}
      <Button type="submit" disabled={saving} className="bg-sol-gold text-black md:col-span-3" data-testid={IDS.giftSubmit}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("gp.createAndSend")}
      </Button>
    </form>
  );
}

const VOID_REASON_MIN = 3;
const VOID_REASON_MAX = 500;

function VoidDialog({ card, onDone, onCancel, t }) {
  const tc = useT("common");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const valid = reason.trim().length >= VOID_REASON_MIN;

  const confirm = async () => {
    setSaving(true);
    try {
      await voidGiftCard(card.gift_card_id, reason.trim());
      toast.success(t("gp.giftVoided"));
      onDone();
    } catch (err) {
      setError(platformOfferErrorMessage(err, tc, t("gp.error")));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
        className="bg-white rounded-2xl p-6 max-w-md w-full" data-testid="gift-void-dialog">
        <h2 className="font-semibold text-slate-800 mb-1">{t("gp.voidTitle", { code: card.code })}</h2>
        <p className="text-sm text-slate-500 mb-4">{t("gp.voidBody")}</p>
        <textarea value={reason} onChange={(e) => { setReason(e.target.value); setError(null); }}
          minLength={VOID_REASON_MIN} maxLength={VOID_REASON_MAX} rows={3}
          placeholder={t("gp.voidReasonPlaceholder")} className={FIELD} data-testid={IDS.giftVoidReason} />
        {error && <p role="alert" className="mt-2 text-sm text-rose-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={saving}>{t("gp.cancel")}</Button>
          <Button onClick={confirm} disabled={!valid || saving} data-testid={IDS.giftVoidConfirm}
            className="bg-rose-600 text-white hover:bg-rose-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("gp.voidConfirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── redemptions ──────────────────────────────────────────────────────────────

/** Who spent a code, where, and where each attempt stands (§4.1 / §4.2 redemptions). */
function RedemptionsDrawer({ title, load, onClose, t }) {
  const tc = useT("common");
  const { lang } = useLanguage();
  const dateText = useDateText();
  const [items, setItems] = useState([]);
  const read = useCallback(async () => { setItems((await load()).items); }, [load]);
  const { loading, loadError, reload } = useAdminLoad(read);

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <aside onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
        className="bg-white w-full max-w-xl h-full overflow-y-auto p-6 shadow-xl" data-testid={IDS.redemptionsDrawer}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-800">{t("gp.redemptionsTitle", { code: title })}</h2>
          <button type="button" onClick={onClose} aria-label={t("gp.close")} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        <LoadFailure
          message={loadError ? extractApiError(loadError, tc("errors.loadFailed"), tc) : ""}
          onRetry={reload}
          retryLabel={tc("actions.retry")}
          testId={`${IDS.redemptionsDrawer}-load-error`}
        />
        {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
        {!loading && !loadError && items.length === 0 && (
          <p className="text-sm text-slate-400">{t("gp.redemptionsEmpty")}</p>
        )}
        <ul className="divide-y divide-slate-100">
          {items.map((r) => (
            <li key={r.redemption_id} className="py-3 text-sm" data-testid={IDS.redemptionRow(r.redemption_id)} data-status={r.status}>
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-slate-800">
                  {r.practice_deleted
                    ? t("gp.redemptionDeletedPractice", { name: r.tenant_name || r.tenant_id })
                    : r.tenant_name || r.tenant_id}
                </span>
                <span className={`text-xs rounded-full px-2 py-0.5 ${REDEMPTION_STATUS_TONES[r.status]}`}>
                  {tc(REDEMPTION_STATUS_KEYS[r.status])}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {tc(SURFACE_KEYS[r.surface])}
                {r.amount_cents != null && ` · ${formatMoneyIntl(r.amount_cents, r.currency, lang)}`}
                {r.demo && ` · ${t("gp.demo")}`}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {t("gp.heldAt", { date: dateText(r.held_at) })}
                {r.applied_at && ` · ${t("gp.appliedAt", { date: dateText(r.applied_at) })}`}
                {r.released_at && ` · ${t("gp.releasedAt", { date: dateText(r.released_at) })}`}
                {r.release_reason && ` · ${tc(RELEASE_REASON_KEYS[r.release_reason])}`}
              </div>
              {r.stripe_ref && <div className="text-[11px] text-slate-400 mt-0.5 font-mono">{r.stripe_ref}</div>}
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
